// Google OAuth2 Service for Drive API
// This service handles OAuth2 authentication for Google Drive

declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  scope: string;
  token_type: string;
}

class GoogleAuthService {
  private static instance: GoogleAuthService;
  private tokenClient: any;
  private accessToken: string | null = null;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): GoogleAuthService {
    if (!this.instance) {
      this.instance = new GoogleAuthService();
    }
    return this.instance;
  }

  async initialize(): Promise<boolean> {
    if (this.isInitialized) {
      return true;
    }

    // Wait for Google Identity Services to load
    return new Promise((resolve) => {
      const checkGoogle = () => {
        if (window.google?.accounts?.oauth2) {
          this.initializeTokenClient();
          this.isInitialized = true;
          resolve(true);
        } else {
          setTimeout(checkGoogle, 100);
        }
      };
      checkGoogle();
    });
  }

  private initializeTokenClient() {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
      console.error('Google Client ID not configured');
      return;
    }

    this.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: 'https://www.googleapis.com/auth/drive.file',
      callback: (response: TokenResponse) => {
        if (response.access_token) {
          this.accessToken = response.access_token;
          console.log('Successfully obtained Google Drive access token');
          
          // Store token expiry
          const expiryTime = Date.now() + (response.expires_in * 1000);
          localStorage.setItem('google_token_expiry', expiryTime.toString());
          
          // Don't store the actual token in localStorage for security
          // Just flag that we have a valid token
          localStorage.setItem('google_auth_status', 'authenticated');
        }
      },
    });
  }

  async requestAccessToken(): Promise<string | null> {
    if (!this.tokenClient) {
      console.error('Token client not initialized');
      return null;
    }

    // Check if we have a valid token
    const tokenExpiry = localStorage.getItem('google_token_expiry');
    if (tokenExpiry && parseInt(tokenExpiry) > Date.now() && this.accessToken) {
      return this.accessToken;
    }

    return new Promise((resolve) => {
      const originalCallback = this.tokenClient.callback;
      this.tokenClient.callback = (response: TokenResponse) => {
        originalCallback(response);
        resolve(response.access_token || null);
      };
      
      // Request token with user consent
      this.tokenClient.requestAccessToken({ prompt: '' });
    });
  }

  async uploadToDrive(fileName: string, content: string, mimeType: string = 'text/html'): Promise<boolean> {
    const token = await this.requestAccessToken();
    if (!token) {
      console.error('No access token available');
      return false;
    }

    const folderId = import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID;
    
    try {
      // Create file metadata
      const metadata = {
        name: fileName,
        mimeType: mimeType,
        ...(folderId && { parents: [folderId] })
      };

      // Create multipart upload body
      const boundary = '-------314159265358979323846';
      const delimiter = "\r\n--" + boundary + "\r\n";
      const close_delim = "\r\n--" + boundary + "--";

      const multipartRequestBody =
        delimiter +
        'Content-Type: application/json\r\n\r\n' +
        JSON.stringify(metadata) +
        delimiter +
        'Content-Type: ' + mimeType + '\r\n\r\n' +
        content +
        close_delim;

      const response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': `multipart/related; boundary="${boundary}"`
        },
        body: multipartRequestBody
      });

      if (response.ok) {
        const data = await response.json();
        console.log('File uploaded to Google Drive:', data.name);
        console.log('File ID:', data.id);
        console.log('Web View Link:', `https://drive.google.com/file/d/${data.id}/view`);
        return true;
      } else {
        const error = await response.text();
        console.error('Failed to upload to Google Drive:', response.status, error);
        return false;
      }
    } catch (error) {
      console.error('Error uploading to Google Drive:', error);
      return false;
    }
  }

  isAuthenticated(): boolean {
    const authStatus = localStorage.getItem('google_auth_status');
    const tokenExpiry = localStorage.getItem('google_token_expiry');
    
    if (authStatus === 'authenticated' && tokenExpiry) {
      return parseInt(tokenExpiry) > Date.now();
    }
    
    return false;
  }

  signOut() {
    this.accessToken = null;
    localStorage.removeItem('google_auth_status');
    localStorage.removeItem('google_token_expiry');
    console.log('Signed out from Google Drive');
  }
}

export default GoogleAuthService;